// V3 Orders Console — ordini + settle esclusivamente su /api/v2
// (cap_orders + cap_payments + core.resources/services). Console
// restaurant nativa: apri ordine su tavolo, aggiungi righe, incassa.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/session';

type Row = Record<string, any>;

const card: React.CSSProperties = { background: 'var(--sk-card, #fff)', borderRadius: 12, padding: 16, border: '1px solid var(--sk-border, #e5e7eb)' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--sk-border, #d1d5db)', fontSize: 14 };
const btn = (bg = '#111827'): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 8, background: bg, color: '#fff', border: 0, cursor: 'pointer', fontSize: 13 });
const badge = (s: string): React.CSSProperties => ({
  padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
  background: s === 'open' ? '#dbeafe' : s === 'settled' ? '#dcfce7' : s === 'production' ? '#fef9c3' : '#f3f4f6',
  color: s === 'open' ? '#1e40af' : s === 'settled' ? '#166534' : s === 'production' ? '#854d0e' : '#4b5563',
});

const orderTotal = (o: Row) => (o.items || []).reduce((t: number, i: Row) => t + Number(i.unit_price || 0) * Number(i.qty || 1), 0);

export function V3OrdersPage() {
  const [orders, setOrders] = useState<Row[]>([]);
  const [resources, setResources] = useState<Row[]>([]);
  const [services, setServices] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('open');
  const [form, setForm] = useState({ resource_id: '', service_mode: 'dine_in', guests: '', note: '' });
  const [cart, setCart] = useState<{ service_id: number; name: string; price: number; qty: number }[]>([]);
  const [selService, setSelService] = useState('');

  const load = useCallback(async () => {
    try {
      const [o, r, s] = await Promise.all([
        apiFetch(`/api/v2/orders${filter ? `?status=${filter}` : ''}`),
        apiFetch('/api/v2/resources'), apiFetch('/api/v2/services'),
      ]);
      setOrders(await o.json()); setResources(await r.json()); setServices(await s.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  const addItem = () => {
    const svc = services.find((s) => s.id === Number(selService));
    if (!svc) return;
    const price = Number(svc.base_price ?? svc.price ?? 0);
    setCart((c) => {
      const ex = c.find((i) => i.service_id === svc.id);
      return ex ? c.map((i) => i.service_id === svc.id ? { ...i, qty: i.qty + 1 } : i)
        : [...c, { service_id: svc.id, name: svc.name, price, qty: 1 }];
    });
    setSelService('');
  };

  const createOrder = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/orders', {
        method: 'POST',
        body: JSON.stringify({
          resource_id: form.resource_id ? Number(form.resource_id) : null,
          service_mode: form.service_mode, guests: form.guests ? Number(form.guests) : null,
          note: form.note || null,
          items: cart.map((i) => ({ service_id: i.service_id, name_snapshot: i.name, unit_price: i.price, qty: i.qty })),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setForm({ resource_id: '', service_mode: 'dine_in', guests: '', note: '' }); setCart([]);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore ordine');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: number, status: string) => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch(`/api/v2/orders/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore stato');
    } finally {
      setSaving(false);
    }
  };

  const settle = async (o: Row) => {
    setSaving(true); setError('');
    try {
      const pay = await apiFetch('/api/v2/payments', {
        method: 'POST',
        body: JSON.stringify({ order_id: o.id, amount: orderTotal(o), method: 'cash', status: 'completed' }),
      });
      if (!pay.ok) throw new Error((await pay.json().catch(() => ({}))).error || pay.statusText);
      const res = await apiFetch(`/api/v2/orders/${o.id}`, { method: 'PATCH', body: JSON.stringify({ status: 'settled' }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore settle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(300px, 380px) 1fr' }}>
      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Nuovo ordine</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <select style={input} value={form.resource_id} onChange={(e) => setForm({ ...form, resource_id: e.target.value })}>
              <option value="">Tavolo / risorsa…</option>
              {resources.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select style={input} value={form.service_mode} onChange={(e) => setForm({ ...form, service_mode: e.target.value })}>
              <option value="dine_in">Sala</option>
              <option value="takeaway">Asporto</option>
              <option value="delivery">Delivery</option>
            </select>
            <input style={input} placeholder="Coperti" type="number" min={0} value={form.guests}
              onChange={(e) => setForm({ ...form, guests: e.target.value })} />
            <input style={input} placeholder="Nota" value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
            <div style={{ display: 'flex', gap: 8 }}>
              <select style={{ ...input, flex: 1 }} value={selService} onChange={(e) => setSelService(e.target.value)}>
                <option value="">Aggiungi piatto…</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name} · €{s.base_price ?? s.price}</option>)}
              </select>
              <button style={btn()} onClick={addItem} disabled={!selService}>+</button>
            </div>
            {cart.map((i) => (
              <div key={i.service_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>{i.qty}× {i.name}</span>
                <span style={{ display: 'flex', gap: 6 }}>
                  <button style={{ ...btn('#e5e7eb'), color: '#111', padding: '2px 8px' }}
                    onClick={() => setCart((c) => i.qty > 1 ? c.map((x) => x.service_id === i.service_id ? { ...x, qty: x.qty - 1 } : x) : c.filter((x) => x.service_id !== i.service_id))}>−</button>
                  €{(i.price * i.qty).toFixed(2)}
                </span>
              </div>
            ))}
            <button style={btn('#0d6efd')} disabled={saving || cart.length === 0} onClick={() => void createOrder()}>
              {saving ? 'Creazione…' : `Apri ordine · €${cart.reduce((t, i) => t + i.price * i.qty, 0).toFixed(2)}`}
            </button>
          </div>
        </div>
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        <p className="sk-muted" style={{ fontSize: 12 }}>
          API: /api/v2 · {orders.length} ordini · {resources.length} risorse · {services.length} servizi
        </p>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ margin: 0, fontSize: 14 }}>Ordini ({orders.length})</h4>
          <select style={{ ...input, width: 'auto' }} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">Tutti</option>
            <option value="open">Aperti</option>
            <option value="production">In produzione</option>
            <option value="settled">Incassati</option>
          </select>
        </div>
        {orders.length === 0 && <p className="sk-muted">Nessun ordine.</p>}
        {orders.map((o) => (
          <div key={o.id} style={{ padding: '10px 0', borderTop: '1px solid var(--sk-border, #eee)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
              <span>
                <strong>#{o.id}</strong>{o.resource_name ? ` · ${o.resource_name}` : ''}
                {o.guests ? ` · ${o.guests} cop.` : ''} · {o.service_mode}
              </span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={badge(o.status)}>{o.status}</span>
                <strong>€{orderTotal(o).toFixed(2)}</strong>
              </span>
            </div>
            {(o.items || []).length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                {(o.items as Row[]).map((i) => `${i.qty}× ${i.name_snapshot}`).join(' · ')}
              </div>
            )}
            {o.status === 'open' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button style={btn('#854d0e')} disabled={saving} onClick={() => void setStatus(Number(o.id), 'production')}>In produzione</button>
                <button style={btn('#166534')} disabled={saving} onClick={() => void settle(o)}>Incassa (cash)</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
